// src/components/content/LinkPreviewCard.jsx
"use client";

import Link from "next/link";

import VideoEmbedCard from "@/components/content/VideoEmbedCard";
import { getMediaEmbed } from "@/lib/mediaEmbed.mjs";

function getHostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch {
    return url;
  }
}

export default function LinkPreviewCard({ preview, className = "" }) {
  if (!preview || typeof preview !== "object") return null;

  const href = preview.url || "#";
  const hostname = getHostname(href);
  const previewTitle = typeof preview.title === "string" ? preview.title.trim() : "";
  const title = previewTitle || href;
  const description = preview.description || "";
  const favicon = preview.favicon || "";
  const imageUrl = preview.imageUrl || "";
  const siteName = preview.siteName || getHostname(href);
  // Re-derive provider identity from the persisted URL for legacy safety.
  const mediaEmbed = getMediaEmbed(href, {
    posterUrl: imageUrl,
    // A raw provider URL is useful for generic previews, but never as media-card copy.
    title: previewTitle,
    authorName: preview.mediaEmbed?.authorName || (description.startsWith("by ") ? description.slice(3) : ""),
    aspectRatio: preview.mediaEmbed?.aspectRatio || "",
  });

  if (mediaEmbed) return <VideoEmbedCard embed={mediaEmbed} preview={preview} className={className} />;

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`block overflow-hidden rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-1)] transition hover:border-[var(--brand)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-1)] ${className}`}
    >
      {imageUrl ? (
        <div className="aspect-[2/1] w-full overflow-hidden bg-[var(--surface-2)]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={imageUrl} alt="" className="h-full w-full object-cover" />
        </div>
      ) : null}
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs text-[var(--muted)]">
          {favicon ? (
            <img
              src={favicon}
              alt=""
              onError={(event) => { event.currentTarget.style.display = "none"; }}
              className="h-4 w-4 rounded-sm"
            />
          ) : null}
          <span className="truncate">{siteName}</span>
        </div>
        <div className="text-sm font-semibold text-[var(--fg)] line-clamp-2">{title}</div>
        {description ? (
          <div className="text-sm text-[var(--muted)] line-clamp-3">{description}</div>
        ) : null}
      </div>
    </Link>
  );
}
